// File: src/pages/Login.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { nativeGoogleToFirebase, nativeAppleToFirebase } from "../nativeSocialAuth";
import { NativeBiometric } from "@capgo/capacitor-native-biometric";
import api, {
  setAccessToken,
  saveNativeRefreshToken,
  deleteNativeRefreshToken,
} from "../api";
import {
  googleProvider,
  appleProvider,
  startProviderSignIn,
  finishRedirectSignInIfAny,
} from "../firebase";
import { FcGoogle } from "react-icons/fc";
import { FaApple } from "react-icons/fa";
import { afTrack, afSetUserId } from "../analytics/appsflyer";

// Only honor in-app destinations; never send authentication redirects off-site.
function safeInAppRedirect(value) {
  if (
    typeof value !== "string" ||
    !value.startsWith("/") ||
    value.startsWith("//") ||
    /[\\\r\n]/.test(value)
  ) return null;
  return value;
}

export default function Login({ setToken, setUserInfo, fetchUserInfo }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [busySocial, setBusySocial] = useState(false);
  const [busyBio, setBusyBio] = useState(false);
  const [busyEmail, setBusyEmail] = useState(false);

  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");

  const [notice, setNotice] = useState("");
  const [resendEmail, setResendEmail] = useState("");
  const [showResend, setShowResend] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const redirectPath = useMemo(() => {
    const fromUrl =
      new URLSearchParams(location.search).get("redirect");

    const safeFromUrl = safeInAppRedirect(fromUrl);
    if (safeFromUrl) return safeFromUrl;

    try {
      const raw = localStorage.getItem(
        "satstacker_signup_intent"
      );

      if (!raw) return null;

      const saved = JSON.parse(raw);

      if (
        saved?.intent === "smart_timing_trial" &&
        saved?.return_to
      ) {
        return safeInAppRedirect(saved.return_to);
      }
    } catch {}

    return null;
  }, [location.search]);

  const isSmartTimingLogin =
    redirectPath === "/plans?smartTiming=1";

  const showApple = useMemo(() => {
    if (!Capacitor.isNativePlatform()) return true;
    return Capacitor.getPlatform() !== "android";
  }, []);

  const anyBusy = busySocial || busyBio || busyEmail;

  const postLoginDest = (fallback) =>
    redirectPath || fallback || "/dashboard";

  const didTrackView = useRef(false);
  useEffect(() => {
    if (didTrackView.current) return;
    afTrack("login_viewed", {});
    didTrackView.current = true;
  }, []);

  // Seed calc_prefill from URL params in case user came via calculator → signup → login
  useEffect(() => {
    try {
      const p = new URLSearchParams(location.search);
      const amount = p.get("amount");
      const freq = p.get("freq");
      const extra_sats = p.get("extra_sats");
      const extra_pct = p.get("extra_pct");
      if (amount && freq) {
        localStorage.setItem("calc_prefill", JSON.stringify({
          amount: Number(amount),
          freq,
          extra_sats: extra_sats ? Number(extra_sats) : null,
          extra_pct: extra_pct ? Number(extra_pct) : null,
        }));
      }
    } catch {}
  }, []);

  async function persistAccessToken(accessToken) {
    // Access JWT goes into module memory on web.
    // Native continues to persist it via api.ts.
    setAccessToken(accessToken);

    // These are harmless UX flags — NOT credentials.
    try {
      const storage = remember
        ? localStorage
        : sessionStorage;

      if (!storage.getItem("ever_logged_in")) {
        storage.setItem("firstLogin", "true");
        storage.setItem("ever_logged_in", "1");
      }

      storage.setItem(
        "has_logged_in_before",
        "true"
      );
    } catch {}

    setToken(accessToken);
    await fetchUserInfo(accessToken);
  }

  async function setAppsFlyerUserIdFromToken(accessToken) {
    try {
      const { data } = await api.get("/me", {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (data?.id != null) await afSetUserId(String(data.id));
    } catch (e) {
      console.warn("[AppsFlyer] failed to set user id on login", e);
    }
  }

  async function exchangeFirebaseForApiToken(user) {
    const idToken = await user.getIdToken();
    const { data } = await api.post("/auth/firebase", {
      id_token: idToken,
      remember_me: remember,
      login_only: true,
    });

    if (!data?.access_token) {
      throw new Error("Backend did not return access_token from /auth/firebase");
    }

    if (Capacitor.isNativePlatform()) {
      await deleteNativeRefreshToken();

      if (remember && data?.refresh_token) {
        await saveNativeRefreshToken(
          data.refresh_token,
          user?.email || ""
        );
      }
    }

    return data.access_token;
  }

  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    (async () => {
      try {
        afTrack("login_started", { method: "redirect" });
        const result = await finishRedirectSignInIfAny();
        const user = result?.user;
        if (!user) return;
        setBusySocial(true);
        setError("");
        const accessToken = await exchangeFirebaseForApiToken(user);
        await persistAccessToken(accessToken);
        await setAppsFlyerUserIdFromToken(accessToken);
        afTrack("login_success", { method: "redirect" });
        navigate(postLoginDest("/dashboard"), { replace: true });
      } catch (e) {
        setError(`Redirect completion failed: ${e?.message || String(e)}`);
        afTrack("login_failed", { method: "redirect", error: e?.message || String(e) });
      } finally {
        setBusySocial(false);
      }
    })();
  }, []);

  const withTimeout = (p, ms, label) =>
    Promise.race([p, new Promise((_, rej) => setTimeout(() => rej(new Error(`${label} timed out after ${ms}ms`)), ms))]);

  const handleSocialLogin = async (providerLabel) => {
    if (anyBusy) return;
    setBusySocial(true);
    afTrack("login_started", { method: providerLabel.toLowerCase() });
    setError("");
    const native = Capacitor.isNativePlatform();
    try {
      let cred;
      if (native) {
        cred = providerLabel === "Google"
          ? await withTimeout(nativeGoogleToFirebase(), 25000, "nativeGoogleToFirebase")
          : await withTimeout(nativeAppleToFirebase(), 25000, "nativeAppleToFirebase");
      } else {
        cred = providerLabel === "Google"
          ? await startProviderSignIn(googleProvider)
          : await startProviderSignIn(appleProvider);
      }
      const user = cred?.user;
      if (!user) throw new Error("No Firebase user returned");
      const accessToken = await withTimeout(exchangeFirebaseForApiToken(user), 25000, "exchangeFirebaseForApiToken");
      await withTimeout(persistAccessToken(accessToken), 25000, "persistAccessToken");
      await setAppsFlyerUserIdFromToken(accessToken);
      afTrack("login_success", { method: providerLabel.toLowerCase() });
      navigate(postLoginDest("/dashboard"));
    } catch (e) {
      if (e?.response?.status === 404) {
        // Preserve a paid-landing Smart Timing destination on signup fallback.
        navigate(
          redirectPath
            ? `/signup?redirect=${encodeURIComponent(redirectPath)}`
            : "/signup"
        );
        return;
      }
      setError(`${providerLabel} sign-in failed: ${e?.message || "Please try again."}`);
    } finally {
      setBusySocial(false);
    }
  };

  const handleBiometricLogin = async () => {
    if (anyBusy) return;
    afTrack("login_started", { method: "biometric" });
    setBusyBio(true);
    setError("");
    try {
      if (!Capacitor.isNativePlatform()) { setError("Biometric login is only available in the mobile app."); return; }
      await NativeBiometric.verifyIdentity({ reason: "Login to SatStacker", title: "Login", subtitle: "Use Face ID / Fingerprint", description: "Quick secure login" });
      const creds = await NativeBiometric.getCredentials({ server: "satstacker_refresh_v1" });
      const refreshToken = creds?.password;
      if (!refreshToken) { setError("No saved login found. Please login with email/password once."); return; }
      const { data } = await api.post("/auth/refresh", { refresh_token: refreshToken }, { withCredentials: true });
      setAccessToken(data.access_token);
      setToken(data.access_token);
      await fetchUserInfo(data.access_token);
      await setAppsFlyerUserIdFromToken(data.access_token);
      afTrack("login_success", { method: "biometric" });
      if (data.refresh_token) {
        await NativeBiometric.setCredentials({ server: "satstacker_refresh_v1", username: creds?.username || "", password: data.refresh_token });
      }
      navigate(postLoginDest("/dashboard"));
    } catch (e) {
      setError("Biometric login failed. Please try again or use your password.");
      afTrack("login_failed", { method: "biometric", error: e?.message || String(e) });
    } finally {
      setBusyBio(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (anyBusy) return;
    setBusyEmail(true);
    afTrack("login_started", { method: "email" });
    setError("");
    try {
      const form = new URLSearchParams();
      form.append("username", email);
      form.append("password", password);
      form.append("remember_me", remember ? "true" : "false");
      const { data } = await api.post("/token", form, { headers: { "Content-Type": "application/x-www-form-urlencoded" }, withCredentials: true });
      if (Capacitor.isNativePlatform()) {
        await deleteNativeRefreshToken();

        if (remember && data?.refresh_token) {
          await saveNativeRefreshToken(
            data.refresh_token,
            email
          );
        }
      }
      await persistAccessToken(data.access_token);
      await setAppsFlyerUserIdFromToken(data.access_token);
      afTrack("login_success", { method: "email" });
      navigate(postLoginDest("/dashboard"));
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (err?.response?.status === 403) setError(msg || "Please confirm your e-mail before logging in.");
      else if (err?.response?.status === 401) setError(msg || "Wrong email or password.");
      else if (err?.response) setError(msg || "Server error – try again later.");
      else setError("Network error – check your connection.");
      afTrack("login_failed", { method: "email", error: msg || err?.message || "login_failed" });
    } finally {
      setBusyEmail(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4 auth-safe-page">
      <div className="w-full max-w-sm">

        {/* Back */}
        <button
          onClick={() => navigate(-1)}
          disabled={anyBusy}
          className="mb-8 flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-300 transition disabled:opacity-50"
        >
          ← Back
        </button>

        {/* Heading */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-lg font-bold text-white tracking-tight">SatStacker</span>
          </div>
          <h1 className="text-2xl font-bold text-white">
            {isSmartTimingLogin
              ? "Finish setting up Smart Timing"
              : "Welcome back"}
          </h1>

          <p className="text-zinc-500 text-sm mt-1">
            {isSmartTimingLogin
              ? "Log in to continue your free trial."
              : "Sign in to your account to continue stacking"}
          </p>
        </div>

        {/* Biometric (native only) */}
        {Capacitor.isNativePlatform() && (
          <button
            type="button"
            disabled={anyBusy}
            onClick={handleBiometricLogin}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 mb-3 rounded-xl bg-zinc-900 border border-zinc-700 text-white font-semibold text-sm hover:bg-zinc-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busyBio ? "Checking…" : "🔒 Continue with Face ID / Biometrics"}
          </button>
        )}

        {/* Social */}
        <div className="flex flex-col gap-3 mb-5">
          <button
            type="button"
            disabled={anyBusy}
            onClick={() => handleSocialLogin("Google")}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white text-zinc-900 font-semibold text-sm hover:bg-zinc-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FcGoogle size={18} />
            {busySocial ? "Signing in…" : "Continue with Google"}
          </button>

          {showApple && (
            <button
              type="button"
              disabled={anyBusy}
              onClick={() => handleSocialLogin("Apple")}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-zinc-900 text-white font-semibold text-sm border border-zinc-700 hover:bg-zinc-800 transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaApple size={16} />
              {busySocial ? "Signing in…" : "Continue with Apple"}
            </button>
          )}
        </div>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-5">
          <div className="flex-1 h-px bg-zinc-800" />
          <span className="text-zinc-600 text-xs">or continue with email</span>
          <div className="flex-1 h-px bg-zinc-800" />
        </div>

        {/* Email form */}
        <form onSubmit={handleLogin} autoComplete="on" method="post" action="/login" className="flex flex-col gap-3">
          <input
            id="username"
            name="username"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            autoCorrect="off"
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={anyBusy}
            className="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/30 disabled:opacity-60 transition"
          />

          <input
            name="password"
            autoComplete="current-password"
            type="password"
            autoCapitalize="none"
            autoCorrect="off"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={anyBusy}
            className="w-full px-4 py-3 rounded-xl bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 text-sm focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/30 disabled:opacity-60 transition"
          />

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm text-zinc-400 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={remember}
                onChange={(e) => setRemember(e.target.checked)}
                disabled={anyBusy}
                className="w-4 h-4 rounded border-zinc-700 bg-zinc-900 accent-orange-500"
              />
              Stay logged in
            </label>
            <Link to="/forgot-password" className="text-xs text-zinc-500 hover:text-orange-400 transition">
              Forgot password?
            </Link>
          </div>

          <button
            type="submit"
            disabled={anyBusy}
            className={`w-full py-3 rounded-xl font-semibold text-sm transition mt-1
              ${busyEmail ? "bg-zinc-700 text-zinc-400 cursor-not-allowed"
                : anyBusy ? "bg-brand/40 text-black/50 cursor-not-allowed"
                : "bg-orange-500 hover:bg-orange-600 active:bg-orange-700 text-white shadow-lg shadow-orange-500/15"}`}
          >
            {busyEmail ? "Signing in…" : "Log in"}
          </button>
        </form>

        {/* Error */}
        {error && (
          <div role="alert" className="mt-4 px-4 py-3 rounded-xl bg-red-950/50 border border-red-800/50 text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Footer */}
        <div className="mt-7 pt-6 border-t border-zinc-900 flex flex-col gap-3">
          <p className="text-sm text-zinc-500">
            Don't have an account?{" "}
            <Link
              to={redirectPath ? `/signup?redirect=${encodeURIComponent(redirectPath)}` : "/signup"}
              className="text-brand hover:underline font-medium"
            >
              Sign up free
            </Link>
          </p>

          <Link
            to="/partner-start"
            className="text-sm text-zinc-500 hover:text-brand transition"
          >
            Used Smart Timing through a partner?{" "}
            <span className="text-brand font-medium">View your stats</span>
          </Link>
          <button
            type="button"
            disabled={anyBusy}
            onClick={() => {
              setResendEmail(email || "");
              setShowResend(true);
            }}
            className="text-sm text-zinc-600 hover:text-zinc-400 transition text-left disabled:opacity-50 w-fit"
          >
            Resend verification e-mail
          </button>
          {showResend && (
            <div className="flex gap-2 mt-2">
              <input
                type="email"
                placeholder="your@email.com"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                className="flex-1 px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-700 text-white text-sm focus:outline-none focus:border-brand"
              />
              <button
                type="button"
                onClick={async () => {
                  if (!resendEmail) return;
                  try {
                    await api.post("/verify-email/resend", { email: resendEmail });
                    setNotice("If that address exists and isn't verified, we've re-sent the e-mail.");
                    setShowResend(false);
                    setResendEmail("");
                  } catch {
                    setNotice("Couldn't resend right now. Please try again in a bit.");
                  }
                }}
                className="px-3 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold transition"
              >
                Send
              </button>
            </div>
          )}
          {notice && (
            <p className="text-sm text-green-400 mt-2">{notice}</p>
          )}
        </div>

      </div>
    </div>
  );
}